import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  FlatList,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../utils/responsive';
import { useNavigation, useRoute } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../types/navigation';
import { BottomNavBar } from '../../../components/BottomNavBar';
import { loyaltyPointService } from '../../../services/loyaltyPointService';

import { createLogger } from '../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../services/encryption/credentialFields';
const logger = createLogger('LoyaltyPointInput');

const categories = [
  'Retail & Shopping',
  'Travel & Hospitality',
  'Food & Beverage',
  'Credit Cards & Financial',
  'Health & Wellness',
  'Other'
];

const loyaltyPointTypes: { [key: string]: string[] } = {
  'Retail & Shopping': ['Department Store', 'Online Retailer', 'Grocery', 'Electronics', 'Fashion', 'Beauty', 'Home Goods'],
  'Travel & Hospitality': ['Airlines', 'Hotels', 'Car Rental', 'Cruise Lines', 'Travel Booking', 'Tourism'],
  'Food & Beverage': ['Restaurants', 'Coffee Shops', 'Fast Food', 'Food Delivery', 'Grocery Rewards'],
  'Credit Cards & Financial': ['Cash Back', 'Travel Rewards', 'Store Card', 'Points Program', 'Miles Program'],
  'Health & Wellness': ['Pharmacy', 'Fitness', 'Health Food', 'Wellness Apps', 'Medical Services'],
  'Other': ['Entertainment', 'Education', 'Pet Supplies', 'Auto Services', 'Miscellaneous']
};

export const LoyaltyPointInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute();
  const [formData, setFormData] = useState({
    category: '',
    loyaltyPointType: '',
    companyName: '',
    accountNumber: '',
    username: '',
    password: '',
    websiteUrl: '',
    notes: '',
  });
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);

  // Check if editing
  const params = route.params as any;
  const isEditing = params?.loyaltyPoint;

  // Get category from route params if available or load existing loyalty point
  useEffect(() => {
    const params = route.params as any;

    // If editing existing loyalty point
    if (params?.loyaltyPoint) {
      const loyaltyPoint = params.loyaltyPoint;
      // Convert category ID to display name
      const categoryMap: { [key: string]: string } = {
        'retail-shopping': 'Retail & Shopping',
        'travel-hospitality': 'Travel & Hospitality',
        'food-beverage': 'Food & Beverage',
        'credit-cards-financial': 'Credit Cards & Financial',
        'health-wellness': 'Health & Wellness',
        'other': 'Other'
      };
      const categoryName = categoryMap[loyaltyPoint.category] || loyaltyPoint.category;

      setFormData({
        category: categoryName,
        loyaltyPointType: loyaltyPoint.loyaltyPointType || '',
        companyName: loyaltyPoint.companyName || '',
        accountNumber: loyaltyPoint.accountNumber || '',
        username: loyaltyPoint.username || '',
        password: loyaltyPoint.password || '',
        websiteUrl: loyaltyPoint.websiteUrl || '',
        notes: loyaltyPoint.notes || '',
      });
    }
    // If pre-selecting category for new loyalty point
    else if (params?.category) {
      // Convert category ID to display name
      const categoryMap: { [key: string]: string } = {
        'retail-shopping': 'Retail & Shopping',
        'travel-hospitality': 'Travel & Hospitality',
        'food-beverage': 'Food & Beverage',
        'credit-cards-financial': 'Credit Cards & Financial',
        'health-wellness': 'Health & Wellness',
        'other': 'Other'
      };
      const categoryName = categoryMap[params.category] || params.category;
      setFormData(prev => ({ ...prev, category: categoryName }));
    }
  }, [route.params]);

  const handleSubmit = async () => {
    if (!formData.category || !formData.loyaltyPointType || !formData.companyName || !formData.accountNumber) {
      Alert.alert('Missing Information', 'Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    // K5: a login is saved only under a Recovery Code; ask before anything is written.
    if ((formData.accountNumber || formData.username || formData.password) && !(await ensureCredentialWriteKey())) {
      setIsSubmitting(false);
      return;
    }
    try {
      const params = route.params as any;
      const isEditing = params?.loyaltyPoint;

      // Convert category display name back to ID
      const categoryIdMap: { [key: string]: string } = {
        'Retail & Shopping': 'retail-shopping',
        'Travel & Hospitality': 'travel-hospitality',
        'Food & Beverage': 'food-beverage',
        'Credit Cards & Financial': 'credit-cards-financial',
        'Health & Wellness': 'health-wellness',
        'Other': 'other'
      };
      const categoryId = categoryIdMap[formData.category] || formData.category.toLowerCase().replace(/\s+/g, '-');

      const loyaltyData = {
        category: categoryId,
        loyaltyPointType: formData.loyaltyPointType,
        companyName: formData.companyName,
        accountNumber: formData.accountNumber,
        username: formData.username,
        password: formData.password,
        websiteUrl: formData.websiteUrl,
        notes: formData.notes,
      };

      let loyaltyPointId: string;

      if (isEditing) {
        // Update existing loyalty point
        loyaltyPointId = params.loyaltyPoint.id;
        await loyaltyPointService.updateLoyaltyPoint(loyaltyPointId, loyaltyData);
      } else {
        // Create new loyalty point
        loyaltyPointId = await loyaltyPointService.storeLoyaltyPoint(loyaltyData);
      }

      Alert.alert(
        'Success',
        `Loyalty program ${isEditing ? 'updated' : 'saved'} successfully!`,
        [
          {
            text: 'OK',
            onPress: () => {
              if (isEditing) {
                // For editing, go back to data screen with updated data
                const updatedLoyaltyPoint = {
                  id: loyaltyPointId,
                  ...loyaltyData,
                };
                navigation.navigate('Loyalty', {
                  screen: 'LoyaltyPointDataScreen',
                  params: { loyaltyPoint: updatedLoyaltyPoint }
                } as any);
              } else {
                // For new loyalty point, go to dashboard
                navigation.navigate('Loyalty', { screen: 'LoyaltyPointDashboardScreen' } as any);
              }
            }
          }
        ]
      );
    } catch (error) {
      logger.error('Error saving loyalty point:', error);
      Alert.alert('Error', 'Failed to save loyalty program. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategorySelect = (category: string) => {
    setFormData({ ...formData, category, loyaltyPointType: '' });
    setShowCategoryModal(false);
  };

  const handleTypeSelect = (type: string) => {
    setFormData({ ...formData, loyaltyPointType: type });
    setShowTypeModal(false);
  };

  const availableTypes = formData.category ? loyaltyPointTypes[formData.category] || [] : [];

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../assets/icons/keyimage.png')}
          style={styles.backgroundIcon}
        />
        <Image
          source={require('../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Add Loyalty Program</Text>
        <Text style={styles.subtitle}>Secure your loyalty program details</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Category *</Text>
            <TouchableOpacity
              style={styles.dropdownInput}
              onPress={() => setShowCategoryModal(true)}
            >
              <Text style={[styles.inputText, !formData.category && styles.placeholderText]}>
                {formData.category || 'Select Category'}
              </Text>
              <Image
                source={require('../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Program Type *</Text>
            <TouchableOpacity
              style={[styles.dropdownInput, !formData.category && styles.disabledDropdown]}
              onPress={() => formData.category && setShowTypeModal(true)}
              disabled={!formData.category}
            >
              <Text style={[styles.inputText, (!formData.loyaltyPointType || !formData.category) && styles.placeholderText]}>
                {formData.loyaltyPointType || 'Select Program Type'}
              </Text>
              <Image
                source={require('../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, !formData.category && styles.disabledIcon]}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Company/Program Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.companyName}
              onChangeText={(text) => setFormData({ ...formData, companyName: text })}
              placeholder="Ex: Marriott Rewards"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Account/Membership Number 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.accountNumber}
                onChangeText={(text) => setFormData({ ...formData, accountNumber: text })}
                placeholder="Enter Account Number"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showAccountNumber}
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowAccountNumber(!showAccountNumber)}
              >
                <Image
                  source={showAccountNumber ? require('../../../assets/icons/Eye.png') : require('../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Username/Email 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.username}
                onChangeText={(text) => setFormData({ ...formData, username: text })}
                placeholder="Enter Username"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showUsername}
                autoCapitalize="none"
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowUsername(!showUsername)}
              >
                <Image
                  source={showUsername ? require('../../../assets/icons/Eye.png') : require('../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.vaultText}>Stored securely - shared only after EOL and will only be shared with your successor after your End of Life event</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Password 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.password}
                onChangeText={(text) => setFormData({ ...formData, password: text })}
                placeholder="Enter Password"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showPassword}
                autoCapitalize="none"
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowPassword(!showPassword)}
              >
                <Image
                  source={showPassword ? require('../../../assets/icons/Eye.png') : require('../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.vaultText}>Stored securely - shared only after EOL and will only be shared with your successor after your End of Life event</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website/URL</Text>
            <TextInput
              style={styles.input}
              value={formData.websiteUrl}
              onChangeText={(text) => setFormData({ ...formData, websiteUrl: text })}
              placeholder="Ex: www.marriott.com"
              placeholderTextColor="#A9A9A9"
              autoCapitalize="none"
              keyboardType="url"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Notes</Text>
            <TextInput
              style={[styles.input, styles.notesInput]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Add any additional information..."
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <TouchableOpacity
            style={[styles.submitButton, isSubmitting && styles.disabledButton]}
            onPress={handleSubmit}
            disabled={isSubmitting}
          >
            <Text style={styles.submitButtonText}>
              {isSubmitting ? 'Saving...' : (isEditing ? 'Update' : 'Submit')}
            </Text>
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      {/* Category Modal */}
      <Modal
        visible={showCategoryModal}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Category</Text>
            <FlatList
              data={categories}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.categoryItem}
                  onPress={() => handleCategorySelect(item)}
                >
                  <Text style={styles.categoryText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowCategoryModal(false)}
            >
              <Text style={styles.closeButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Type Modal */}
      <Modal
        visible={showTypeModal}
        transparent={true}
        animationType="slide"
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Program Type</Text>
            <FlatList
              data={availableTypes}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.categoryItem}
                  onPress={() => handleTypeSelect(item)}
                >
                  <Text style={styles.categoryText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowTypeModal(false)}
            >
              <Text style={styles.closeButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundIcon: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    opacity: 0.9,
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    padding: ms(20),
    paddingBottom: ms(40),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  inputGroup: {
    marginBottom: ms(20),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(30),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    paddingHorizontal: ms(15),
    height: s(50),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  notesInput: {
    height: s(100),
    paddingTop: ms(15),
    textAlignVertical: 'top',
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  dropdownInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(30),
    paddingHorizontal: ms(15),
    height: s(50),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  disabledDropdown: {
    backgroundColor: '#F5F5F5',
    borderColor: '#e8e8e8',
  },
  disabledIcon: {
    opacity: 0.5,
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    color: '#A9A9A9',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1C2C4F',
  },
  submitButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    padding: ms(15),
    alignItems: 'center',
    marginTop: ms(20),
  },
  disabledButton: {
    backgroundColor: '#CCCCCC',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '500',
  },
  bottomPadding: {
    height: s(150),
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(20),
    padding: ms(20),
    width: '80%',
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: ms(15),
    textAlign: 'center',
  },
  categoryItem: {
    padding: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#EEEEEE',
  },
  categoryText: {
    fontSize: ms(16, 0.3),
    color: '#333333',
  },
  closeButton: {
    marginTop: ms(15),
    padding: ms(15),
    alignItems: 'center',
    backgroundColor: '#F3F6F8',
    borderRadius: ms(25),
  },
  closeButtonText: {
    color: '#333333',
    fontSize: ms(16, 0.3),
    fontWeight: '500',
  },
});